"use client"

import { useState, useEffect, useCallback, type ReactNode } from "react"
import ProductModel from "@src/models/product"
import { Icons, NextImage } from "@src/components"
import { NO_IMAGE } from "@src/components/NextImage"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Image from "next/image"
import ProductImageLightbox from "./product-image-lightbox"
import {
  PDP_THUMB_W,
  PDP_THUMB_H,
  PDP_THUMB_GAP,
  getProductGalleryImages
} from "./gallery-constants"
import { urlPathname } from "@src/utils/unique"

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".ogg", ".avi", ".mkv"]

function isVideoUrl(url: string): boolean {
  const pathname = urlPathname(url).toLowerCase()
  return VIDEO_EXTENSIONS.some(ext => pathname.endsWith(ext))
}

interface Props {
  detail: ProductModel
  selectedImage: string
  overlay?: ReactNode
}

const Thumb = ({
  image,
  alt,
  selected,
  onClick,
  isVideo,
  poster
}: {
  image: string
  alt: string
  selected: boolean
  onClick?: () => void
  isVideo?: boolean
  poster?: string
}) => (
  <Box
    width={PDP_THUMB_W}
    height={PDP_THUMB_H}
    borderRadius="8px"
    overflow="hidden"
    onClick={onClick}
    sx={{
      cursor: onClick ? "pointer" : "default",
      flexShrink: 0,
      boxSizing: "border-box",
      border: selected ? "2px solid #000" : "1px solid transparent",
      "&:hover": onClick ? { border: "2px solid #000" } : {},
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#F6F6F6"
    }}
  >
    <Image
      width={PDP_THUMB_W}
      height={PDP_THUMB_H}
      src={isVideo && poster ? poster : image}
      style={{ objectFit: "cover" }}
      alt={alt}
    />
    {isVideo && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.3)",
          "& svg": { color: "#fff", fontSize: 28 }
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      </Box>
    )}
  </Box>
)

const ImageCarouselDesktop = ({ detail, selectedImage, overlay }: Props) => {
  const galleryImages = getProductGalleryImages(detail)
  const productName = detail?.name || ""

  const [currentIndex, setCurrentIndex] = useState(0)
  const [colorSrc, setColorSrc] = useState("")
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [thumbStartIndex, setThumbStartIndex] = useState(0)

  const mainSrc =
    colorSrc || galleryImages[currentIndex] || galleryImages[0] || NO_IMAGE
  const isMainVideo = isVideoUrl(mainSrc)
  const isZoomable = !isMainVideo && mainSrc !== NO_IMAGE

  const posterImage = !isMainVideo
    ? undefined
    : detail?.video_thumbnail ||
      galleryImages.find(img => !isVideoUrl(img)) ||
      undefined

  const activeIndex = colorSrc
    ? galleryImages.findIndex(
        image => urlPathname(image) === urlPathname(colorSrc)
      )
    : currentIndex

  useEffect(() => {
    if (selectedImage) {
      setColorSrc(selectedImage)
      const selectedIndex = galleryImages.findIndex(
        image => urlPathname(image) === urlPathname(selectedImage)
      )
      setThumbStartIndex(Math.max(selectedIndex - 3, 0))
      return
    }
    setColorSrc("")
    setCurrentIndex(0)
    setThumbStartIndex(0)
  }, [detail?.id, selectedImage])

  const selectIndex = useCallback((index: number) => {
    setCurrentIndex(index)
    setColorSrc("")
    setThumbStartIndex(startIndex => {
      if (index < startIndex) return index
      if (index >= startIndex + 4) return index - 3
      return startIndex
    })
  }, [])

  const handlePrevSlide = useCallback(() => {
    setThumbStartIndex(index => Math.max(index - 1, 0))
  }, [])

  const handleNextSlide = useCallback(() => {
    setThumbStartIndex(index =>
      Math.min(index + 1, Math.max(galleryImages.length - 4, 0))
    )
  }, [galleryImages.length])

  const lightboxImages = (
    mainSrc !== NO_IMAGE && !galleryImages.includes(mainSrc)
      ? [mainSrc, ...galleryImages]
      : galleryImages
  ).filter(src => !isVideoUrl(src))

  const lightboxIndex = Math.max(lightboxImages.indexOf(mainSrc), 0)
  const showThumbNav = galleryImages.length > 4
  const isBeginning = thumbStartIndex === 0
  const isEnd = thumbStartIndex >= Math.max(galleryImages.length - 4, 0)
  const visibleThumbs = galleryImages.slice(
    thumbStartIndex,
    thumbStartIndex + 4
  )

  return (
    <Stack direction="row" gap="32px" width="100%" alignItems="flex-start">
      <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
        <Box
          onClick={() => {
            if (isZoomable) setLightboxOpen(true)
          }}
          role="button"
          tabIndex={0}
          aria-label="Zoom product image"
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              if (isZoomable) setLightboxOpen(true)
            }
          }}
          sx={{
            position: "relative",
            flex: 1,
            width: "100%",
            aspectRatio: "1 / 1",
            cursor: isZoomable ? "zoom-in" : "default",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#F6F6F6",
            "&:focus-visible": {
              outline: "2px solid #000",
              outlineOffset: 2
            }
          }}
        >
          <Box
            key={mainSrc}
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1
            }}
          >
            {isMainVideo ? (
              <video
                src={mainSrc}
                autoPlay
                muted
                loop
                playsInline
                poster={posterImage}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8
                }}
              />
            ) : (
              <NextImage
                priority
                sizes="(max-width: 900px) 100vw, min(540px, 40vw)"
                src={mainSrc}
                alt={productName}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& .next-image-placeholder, & .next-image-full": {
                    objectFit: "cover",
                    borderRadius: "8px"
                  }
                }}
              />
            )}
          </Box>
          {!isMainVideo && (
            <Box
              sx={{
                position: "absolute",
                right: 12,
                top: overlay ? 12 : undefined,
                bottom: overlay ? undefined : 12,
                zIndex: 2,
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.9)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.12)"
              }}
            >
              <Icons name="search" />
            </Box>
          )}
        </Box>
        {overlay && (
          <Box
            sx={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              zIndex: 3
            }}
          >
            {overlay}
          </Box>
        )}
      </Box>

      {galleryImages.length > 0 && (
        <Box
          sx={{
            position: "relative",
            width: PDP_THUMB_W,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch"
          }}
        >
          {showThumbNav && !isBeginning && (
            <Stack
              justifyContent="center"
              sx={{
                position: "absolute",
                zIndex: 99,
                top: 5,
                left: "50%",
                transform: "translateX(-50%)"
              }}
            >
              <Icons
                sx={{
                  background: "white",
                  "&:hover": { background: "white", opacity: 0.9 }
                }}
                name="arrow-up"
                type="button"
                onClick={handlePrevSlide}
              />
            </Stack>
          )}

          <Stack direction="column" gap={`${PDP_THUMB_GAP}px`}>
            {visibleThumbs.map((image, index) => {
              const galleryIndex = thumbStartIndex + index
              return (
                <Thumb
                  key={image || galleryIndex}
                  image={image}
                  alt={productName}
                  selected={activeIndex === galleryIndex}
                  onClick={() => selectIndex(galleryIndex)}
                  isVideo={isVideoUrl(image)}
                  poster={
                    isVideoUrl(image)
                      ? (detail as any)?.video_thumbnail ||
                        galleryImages.find(img => !isVideoUrl(img))
                      : undefined
                  }
                />
              )
            })}
          </Stack>

          {showThumbNav && (
            <Stack justifyContent="center">
              <Icons
                name="arrow-down"
                type="button"
                disabled={isEnd}
                sx={{ "&:disabled": { opacity: 0.5 } }}
                onClick={handleNextSlide}
              />
            </Stack>
          )}
        </Box>
      )}

      <ProductImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        src={mainSrc}
        alt={productName}
        images={lightboxImages}
        initialIndex={lightboxIndex}
      />
    </Stack>
  )
}

export default ImageCarouselDesktop
